<template>
    <ChartPortlet
        v-if="paramsLoaded"
        ref="chartPortlet"
        color="primary"
        :title="i18n('dashboard.salesChart.chartTitle')"
        chartHeight="180px"
    >
        <template #description>
            <div class="kt-widget20__content kt-portlet__space-x" style="padding-top: 0; text-align: right">
                <span class="kt-widget20__number kt-font-primary">{{ toMoneyWithSign(totalSales, true) }}</span>
                <span class="kt-widget20__desc">
                    {{ toFormattedNumber(orders) }} {{ i18n("dashboard.salesChart.orders") }}
                </span>
                <span class="kt-widget20__desc">
                    {{ toFormattedNumber(payments) }} {{ i18n("dashboard.salesChart.payments") }}
                </span>
            </div>
        </template>
    </ChartPortlet>
</template>

<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import { DashboardDao } from "@/data/dao/service/DashboardDao";
    import type { DashboardStatsPeriod } from "@/data/model/Enums";
    import { i18n } from "@/portal/Trans";
    import { logError } from "@/shared/logging/errors";
    import { toMoneyWithSign } from "@/shared/utils/format";
    import { toFormattedNumber } from "@/shared/utils/format";
    import { Chart } from "chart.js/auto";
    import { color } from "chart.js/helpers";
    import { computed, nextTick, onMounted, ref, watch } from "vue";

    import ChartPortlet from "@/portal/components/dashboard/ChartPortlet.vue";

    import { CHART_COLORS, getGradient } from "./Utils";
    import { type ChartLabelMapping, getChartLabels } from "./chartData";
    import {
        defaultLineChartLayout,
        defaultTooltipSettings,
        displayAssignedTooltipLabel,
        displayPointMoneyValue,
        hiddenLegend,
        moneyScale,
        shortMonthsScale,
    } from "./sharedChartUtils";

    const dao = Factory.getInstance<DashboardDao>(DashboardDao);

    const props = defineProps<{
        locationId?: string;
        period?: DashboardStatsPeriod;
        selectedDate?: Date;
    }>();

    let chart: Chart;
    let chartLabels: ChartLabelMapping[];

    const totalSales = ref(0);
    const payments = ref(0);
    const orders = ref(0);

    const isInitialized = ref(false);
    const chartPortlet = ref<InstanceType<typeof ChartPortlet> | null>(null);
    const canvas = computed(() => chartPortlet.value?.canvasElement);
    const paramsLoaded = computed(() => props.locationId !== undefined && props.period !== undefined);

    // create chart if all params are provided and is first init or init after not all params were provided
    onMounted(initializeChart);
    watch(paramsLoaded, initializeChart);

    // reload data if any prop is changed
    watch(() => props.locationId, refreshData);
    watch(() => props.period, refreshData);
    watch(() => props.selectedDate, refreshData);

    async function initializeChart() {
        if (!paramsLoaded.value) return;

        await nextTick();
        createChart();
        loadData();
    }

    function createChart() {
        const ctx = canvas.value!.getContext("2d")!;
        const gradient = getGradient(ctx, CHART_COLORS.lightBlue);

        chart = new Chart(ctx, {
            type: "line",
            data: {
                labels: [],
                datasets: [
                    {
                        backgroundColor: gradient,
                        borderColor: color(CHART_COLORS.darkBlue).alpha(0.4).rgbString(),
                        cubicInterpolationMode: "monotone",
                        tension: 0.4,
                        fill: "origin",
                        data: [],
                    },
                ],
            },
            options: {
                scales: {
                    x: shortMonthsScale(45),
                    y: moneyScale(),
                },
                ...defaultLineChartLayout,
                plugins: {
                    legend: hiddenLegend,
                    tooltip: {
                        ...defaultTooltipSettings,
                        callbacks: {
                            title: (context) => displayAssignedTooltipLabel(context, chartLabels),
                            label: (context) => displayPointMoneyValue(context),
                        },
                    },
                },
            },
        });

        isInitialized.value = true;
    }

    async function refreshData() {
        if (!isInitialized.value) return;

        loadData();
    }

    async function loadData() {
        try {
            const summary = await dao.getSalesSummary(props.locationId!, props.period!, props.selectedDate);
            totalSales.value = summary.totalAmount;
            payments.value = summary.payments;
            orders.value = summary.orders;

            chartLabels = getChartLabels(summary.dateRanges, summary.granularity);
            chart.data.labels = chartLabels.map((x) => x.scaleLabel);
            chart.data.datasets![0].data = summary.data.map((x) => x.amount);

            chart.update();
        } catch (e) {
            logError(e);
        }
    }
</script>
